import type { MapObject, ObjectDetail, ObjectFileGroup, TileField, TranslationModel } from "@fortex/api";

const pictureTypes = ["FILETYPE_PICTURE_SMALL", "FILETYPE_PICTURE_MEDIUM"];

const trueValues = new Set(["true", "1", "igen", "yes", "t", "y"]);

export function translationValue(model: TranslationModel | null | undefined, langId: number): string | null {
  const values = model?.lang ?? null;

  if (values === null || values.length === 0) return null;

  const preferred = values[langId - 1];

  if (typeof preferred === "string" && preferred.trim().length > 0) return preferred;

  const first = values[0];

  return typeof first === "string" && first.trim().length > 0 ? first : null;
}

export function pictureFileId(groups: ObjectFileGroup[]): string | null {
  for (const group of groups) if (group.thumbnailFileId) return group.thumbnailFileId;

  for (const type of pictureTypes) {
    for (const group of groups) {
      const file = group.files.find(a => a.typeIdName === type);

      if (file) return file.id;
    }
  }

  return null;
}

export function detailToObject(detail: ObjectDetail, langId: number): MapObject {
  return {
    id: detail.id,
    idName: detail.idName,
    name: translationValue(detail.name, langId),
    typeId: detail.type?.id ?? 0,
    typeIdName: detail.type?.idName ?? null,
    typeName: translationValue(detail.type?.name, langId),
    geometryIdName: detail.geometry?.idName ?? null,
    parentId: detail.parent_Id,
    showOnMap: detail.showOnMap,
    publishable: detail.publishable,
    lat: detail.lat,
    lon: detail.lon,
    wkt: detail.wkt,
    memberCount: 1,
    memberIds: [detail.id],
    memberTypeCounts: null
  };
}

export function objectTitle(object: MapObject): string {
  const name = object.name?.trim();

  if (name) return name;

  const idName = object.idName?.trim();

  return idName ? idName : `#${object.id}`;
}

export function visibleFields(fields: TileField[], admin: boolean): TileField[] {
  return fields.filter(a => a.visible === "All" || (a.visible === "Admin" && admin));
}

export function parseBool(value: string | null): boolean {
  return value !== null && trueValues.has(value.trim().toLowerCase());
}

export function dateInputValue(value: string | null): string {
  if (value === null) return "";

  const trimmed = value.trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);

  if (iso !== null) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const dotted = /^(\d{4})\.\s?(\d{1,2})\.\s?(\d{1,2})\.?/.exec(trimmed);

  if (dotted !== null) return `${dotted[1]}-${dotted[2].padStart(2, "0")}-${dotted[3].padStart(2, "0")}`;

  const parsed = new Date(trimmed);

  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().slice(0, 10);
}

export function fieldRows(fields: TileField[], data: { key: string; value: string | null }[], admin: boolean): TileField[] {
  const visible = visibleFields(fields, admin);

  if (visible.length > 0) return visible;

  return data.map(a => ({ parentField: null, key: a.key, humanName: a.key, visible: "All", editable: false, type: "String", filterMapVisible: "None" }));
}
